import React from "react";
import { Link } from "react-router-dom";
import ApplicationNoteDiagram from "../components/ApplicationNoteDiagram";

const cardClasses = "bg-white rounded-xl p-6 shadow-sm border border-gray-100 h-full";

const ApplicationNotePage: React.FC = () => {
  return (
    <div className="bg-white min-h-screen">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold" style={{ color: "#0A2A4A" }}>
            Pureaum
          </Link>
          <Link
            to="/"
            className="text-sm font-semibold hover:underline"
            style={{ color: "#0A2A4A" }}
          >
            Back to homepage
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12 space-y-10">
        <section id="application-note-cloud-intelligence" className="space-y-6">
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: "#0A2A4A" }}>
              Application Note
            </p>
            <h1 className="text-3xl font-bold" style={{ color: "#0A2A4A" }}>
              Application Note: Cloud Scan Intelligence for Food Counterfeit Detection
            </h1>
            <p className="text-sm" style={{ color: "#2B2B2B" }}>
              Counterfeiting and adulteration are recurring and high-velocity in India, especially across dairy and mass
              staples. Recent enforcement actions highlight the scale of the problem. Pureaum&apos;s core advantage is
              intelligent, cloud-based detection. QR is the signal source; intelligence is the defense. Detection to
              prevention to containment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className={`${cardClasses} space-y-3`}>
              <h3 className="text-xl font-bold" style={{ color: "#0A2A4A" }}>
                The Primary Control: Cloud Scan Intelligence (Always-On)
              </h3>
              <ul className="space-y-2 text-sm" style={{ color: "#2B2B2B" }}>
                <li>Duplicate scans of the same ID across different locations</li>
                <li>Geo-velocity anomalies (impossible travel time)</li>
                <li>Suspicious scan clusters around retailers and markets</li>
                <li>Post-consumption scan activity (label harvesting or reuse)</li>
                <li>Automated escalation with timelines, heatmaps, affected IDs/batches</li>
              </ul>
              <p className="text-sm" style={{ color: "#2B2B2B" }}>
                This transforms QR verification from a static check into a live intelligence system.
              </p>
            </div>

            <div className={`${cardClasses} space-y-3`}>
              <h3 className="text-xl font-bold" style={{ color: "#0A2A4A" }}>
                Outer QR: Public Verification Layer (Default)
              </h3>
              <ul className="space-y-2 text-sm" style={{ color: "#2B2B2B" }}>
                <li>Read-only verification for instant consumer trust at point of sale</li>
                <li>Continuous telemetry feeding Cloud Scan Intelligence</li>
                <li>Works even without Inner QR</li>
              </ul>
            </div>

            <div className={`${cardClasses} space-y-3`}>
              <h3 className="text-xl font-bold" style={{ color: "#0A2A4A" }}>
                Optional Inner QR: Second Line of Defense + Loyalty Engine
              </h3>
              <div className="space-y-2 text-sm" style={{ color: "#2B2B2B" }}>
                <p className="font-semibold" style={{ color: "#0A2A4A" }}>
                  Second Line of Defense
                </p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Hidden behind tamper layer</li>
                  <li>Activate or consume proof; makes label reuse economically unviable</li>
                </ul>
                <p className="font-semibold" style={{ color: "#0A2A4A" }}>
                  Loyalty and Engagement
                </p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Verified customers only; warranties, rewards, post-purchase engagement</li>
                  <li>No fake loyalty accounts</li>
                </ul>
                <p className="font-semibold" style={{ color: "#0A2A4A" }}>
                  Configurable Rollout
                </p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Outer-only supported by default</li>
                  <li>Inner QR for premium SKUs or high-risk regions</li>
                  <li>Progressive adoption</li>
                </ul>
              </div>
            </div>

            <div className={`${cardClasses} space-y-3`}>
              <h3 className="text-xl font-bold" style={{ color: "#0A2A4A" }}>
                Practical Outcome
              </h3>
              <p className="text-sm" style={{ color: "#2B2B2B" }}>
                With Outer QR plus Cloud Scan Intelligence: early detection and faster containment.
              </p>
              <p className="text-sm" style={{ color: "#2B2B2B" }}>
                With Optional Inner QR enabled: 90%+ counterfeit-safe in practice.
              </p>
              <p className="text-xs" style={{ color: "#2B2B2B" }}>
                This is a program-level outcome when deployed correctly; no absolute guarantees are claimed.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 overflow-x-auto">
            <h3 className="text-lg font-bold mb-4" style={{ color: "#0A2A4A" }}>
              Cloud Scan Intelligence is the hero; QR is the signal source
            </h3>
            <ApplicationNoteDiagram />
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 overflow-x-auto">
            <h3 className="text-lg font-bold mb-4" style={{ color: "#0A2A4A" }}>
              Layered defense with optional Inner QR
            </h3>
            <svg
              width="100%"
              height="180"
              viewBox="0 0 860 180"
              role="img"
              aria-label="Outer QR default layer and optional Inner QR second line"
            >
              <defs>
                <style>
                  {`.stack{fill:#fff;stroke:#D6E8FB;stroke-width:2;}
                    .title{font:700 13px 'Inter', system-ui; fill:#0A2A4A;}
                    .body{font:600 11px 'Inter', system-ui; fill:#2B2B2B;}
                    .optional{font:700 11px 'Inter', system-ui; fill:#27AE60;}
                    .arrow{stroke:#BFD3EA;stroke-width:3;stroke-linecap:round;}`}
                </style>
              </defs>
              <g transform="translate(40,40)">
                <rect className="stack" x="0" y="0" width="320" height="100" rx="12" />
                <text className="title" x="16" y="26">Outer QR (Default)</text>
                <text className="body" x="16" y="48">Verify at shelf and post-purchase</text>
                <text className="body" x="16" y="66">Telemetry feeds Cloud Scan Intelligence</text>
                <text className="body" x="16" y="84">Operates standalone</text>
              </g>
              <g transform="translate(420,40)">
                <rect className="stack" x="0" y="0" width="320" height="100" rx="12" />
                <text className="title" x="16" y="26">Inner QR (Optional)</text>
                <text className="optional" x="16" y="44">Second line + Loyalty engine</text>
                <text className="body" x="16" y="64">Activate or consume proof</text>
                <text className="body" x="16" y="80">Verified customers lead to loyalty, rewards, warranties</text>
                <text className="body" x="16" y="96">Choose for premium SKUs or high-risk regions</text>
              </g>
              <line className="arrow" x1="360" y1="90" x2="420" y2="90" />
            </svg>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-xl font-bold mb-3" style={{ color: "#0A2A4A" }}>
              What Pureaum Changes
            </h3>
            <ul className="space-y-2 text-sm" style={{ color: "#2B2B2B" }}>
              <li>Cloud Scan Intelligence detects counterfeit behavior early and triggers alerts</li>
              <li>Outer QR enables instant verification and real-world telemetry</li>
              <li>Optional Inner QR adds a second line of defense and verified loyalty</li>
              <li>Outcome: 90%+ counterfeit-safe in practice, without operational complexity</li>
            </ul>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ApplicationNotePage;
